import { getUserPermission } from '@sg/services';
import type { IFlatPermission, IPermissionNode } from '@sg/types/permission';

type TPermissionListener = () => void;

let flatPermissions: Record<string, IFlatPermission> = {};
let isReady = false;
let isLoading = false;
let initPromise: Promise<void> | null = null;
const listeners: TPermissionListener[] = [];

const toBoolean = (value: number | undefined): boolean => value === 1;

export const flattenPermissionTree = (
  nodes: IPermissionNode[],
  result: Record<string, IFlatPermission> = {},
): Record<string, IFlatPermission> => {
  nodes.forEach((node) => {
    result[node.permission_key] = {
      readable: toBoolean(node.readable),
      writable: toBoolean(node.writeable),
      studioIds: node.studio_ids,
    };

    if (node.page?.length) {
      flattenPermissionTree(node.page, result);
    }
  });

  return result;
};

const notifyListeners = (): void => {
  listeners.forEach((listener) => listener());
};

export const subscribePermissionChange = (listener: TPermissionListener): (() => void) => {
  listeners.push(listener);
  return () => {
    const index = listeners.indexOf(listener);
    if (index > -1) {
      listeners.splice(index, 1);
    }
  };
};

export const isPermissionReady = (): boolean => isReady;

export const isPermissionLoading = (): boolean => isLoading;

/** True while the permission API is in flight (not yet ready). */
export const isPermissionPending = (): boolean => !isReady && isLoading;

export const getFlatPermission = (permissionKey: string): IFlatPermission | undefined => (
  flatPermissions[permissionKey]
);

export const initPermissions = (): Promise<void> => {
  if (isReady) {
    return Promise.resolve();
  }

  if (initPromise) {
    return initPromise;
  }

  isLoading = true;
  notifyListeners();

  initPromise = getUserPermission()
    .then((response) => {
      // rawData may return full envelope or inner data depending on httpC config.
      const permissions = response?.data?.permissions ?? response?.permissions;
      if (permissions?.length) {
        flatPermissions = flattenPermissionTree(permissions);
        isReady = true;
      } else {
        console.error('[permission] Invalid user permission response:', response);
      }
    })
    .catch((error) => {
      console.error('[permission] Failed to fetch user permissions:', error);
    })
    .finally(() => {
      isLoading = false;
      notifyListeners();
    });

  return initPromise;
};

export const resetPermissionsForTest = (): void => {
  flatPermissions = {};
  isReady = false;
  isLoading = false;
  initPromise = null;
};
